/**
 * Floating CTA Bar — section-floating-cta-bar.js
 *
 * Behaviour:
 * - Hidden on page load
 * - Appears after the user starts scrolling down (past threshold)
 * - Stays visible for the rest of the page, in both scroll directions
 * - Retracts again at the bottom of the page, where the footer carries its own
 *   links and the bar would only cover what the shopper scrolled down to read
 * - Supports multiple instances via section ID
 */

(function () {
  'use strict';

  const SHOW_ABOVE = 60;  // px scrolled before the bar appears
  const HIDE_BELOW = 40;  // px scrolled below which it hides again (hysteresis)

  const BOTTOM_OFFSET_DEFAULT = 120; // px from the page bottom where it retracts
  const BOTTOM_HYSTERESIS = 40;      // px of slack before it comes back

  // Px between the bottom of the viewport and the bottom of the document.
  // 0 means the page is scrolled all the way down.
  function distanceFromBottom() {
    const doc = document.documentElement;
    const body = document.body;
    const docHeight = Math.max(
      doc.scrollHeight,
      doc.offsetHeight,
      body ? body.scrollHeight : 0,
      body ? body.offsetHeight : 0
    );
    return docHeight - (window.scrollY + window.innerHeight);
  }

  class FloatingCtaBar {
    constructor(el) {
      this.el = el;
      this.visible = false;
      this.ticking = false;

      const offset = parseInt(el.getAttribute('data-bottom-offset'), 10);
      this.bottomOffset = isNaN(offset) ? BOTTOM_OFFSET_DEFAULT : offset;

      this._onScroll = this._onScroll.bind(this);
      window.addEventListener('scroll', this._onScroll, { passive: true });
      // The bottom test depends on viewport height as well as scroll position,
      // so a resize — including an iOS toolbar collapse, or the keyboard — can
      // change the answer without a scroll event.
      window.addEventListener('resize', this._onScroll, { passive: true });

      // Make the CTA navigate on the FIRST tap. The theme defers its JS until the
      // first touch and can swallow the click synthesized from that tap, so a native
      // <a> often needs a second tap on mobile. The theme's own first-tap workaround
      // only covers the very first touch on the page — but this bar only appears
      // after the user has already scrolled, so that workaround is spent by the time
      // the button is tapped. Navigating on pointerup (the real release) sidesteps
      // all of it. Desktop/mouse keeps native <a> behaviour untouched.
      this._bindFirstTapNav();

      // Run once on init in case page loads already scrolled (e.g. back navigation)
      this._update(window.scrollY);
    }

    _bindFirstTapNav() {
      const link = this.el.querySelector('.floating-cta-bar__button');
      if (!link) return;
      let startX = 0, startY = 0, moved = false;
      link.addEventListener('pointerdown', (e) => {
        startX = e.clientX; startY = e.clientY; moved = false;
      }, { passive: true });
      link.addEventListener('pointermove', (e) => {
        if (Math.abs(e.clientX - startX) > 10 || Math.abs(e.clientY - startY) > 10) moved = true;
      }, { passive: true });
      link.addEventListener('pointerup', (e) => {
        if (e.pointerType === 'mouse') return;              // desktop: let native click navigate
        if (moved) return;                                  // was a scroll/drag, not a tap
        if (link.getAttribute('aria-disabled') === 'true') return;
        const href = link.getAttribute('href');
        if (!href || href.charAt(0) === '#') return;        // no real destination
        window.location.href = href;
      });
    }

    _onScroll() {
      if (!this.ticking) {
        window.requestAnimationFrame(() => {
          this._update(window.scrollY);
          this.ticking = false;
        });
        this.ticking = true;
      }
    }

    _update(currentY) {
      // Position-based, NOT direction-based. This used to show on scroll-down and
      // hide on scroll-up, which made the bar vanish exactly when a shopper was
      // heading back up to re-read something — the moment they were most likely to
      // want it. Now it is simply visible anywhere below the threshold and hidden
      // only near the very top, where the hero carries its own CTA.
      //
      // The two thresholds are deliberate hysteresis: iOS rubber-banding around the
      // top of the page would otherwise flip the bar back and forth across a single
      // boundary.
      //
      // The bottom of the page is the other place it retracts: down there the
      // footer carries its own links, and a bar pinned over them just covers
      // what the shopper scrolled all the way down to read. Same hysteresis
      // idea, so a page that happens to end near the threshold cannot flicker.
      const fromBottom = distanceFromBottom();
      if (fromBottom <= this.bottomOffset) {
        this._hide();
        return;
      }

      if (currentY > SHOW_ABOVE && fromBottom > this.bottomOffset + BOTTOM_HYSTERESIS) {
        this._show();
      } else if (currentY < HIDE_BELOW) {
        this._hide();
      }
    }

    _show() {
      if (this.visible) return;
      this.visible = true;
      this.el.classList.add('is-visible');
      this.el.setAttribute('aria-hidden', 'false');
    }

    _hide() {
      if (!this.visible) return;
      this.visible = false;
      this.el.classList.remove('is-visible');
      this.el.setAttribute('aria-hidden', 'true');
    }

    destroy() {
      window.removeEventListener('scroll', this._onScroll);
      window.removeEventListener('resize', this._onScroll);
    }
  }

  // ── Init ──────────────────────────────────────────────────────

  function initBars() {
    document.querySelectorAll('.floating-cta-bar').forEach((el) => {
      if (el._floatingCtaBar) return; // already initialised
      el._floatingCtaBar = new FloatingCtaBar(el);
    });
  }

  // Standard DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initBars);
  } else {
    initBars();
  }

  // Shopify theme editor: re-init when section is loaded/reloaded
  document.addEventListener('shopify:section:load', initBars);
  document.addEventListener('shopify:section:unload', (e) => {
    const el = e.target.querySelector('.floating-cta-bar');
    if (el && el._floatingCtaBar) {
      el._floatingCtaBar.destroy();
      delete el._floatingCtaBar;
    }
  });
})();
